import { Injectable } from '@angular/core';
import { Observable, Subject, Subscription, takeUntil } from 'rxjs';

import { AppUpdateService } from '@shure/cloud/device-management/shared/services';
import {
	apolloErrorContains,
	nonRetryableErrorSubStrings,
	SubscriptionManager,
	SubscriptionManagerConfigCreate
} from '@shure/cloud/shared/apollo';
import { SystemState, SystemStatus } from '@shure/cloud/shared/models/devices';
import { monitorLoginState, OktaInterfaceService } from '@shure/cloud/shared/okta/data-access';
import { ILogger } from '@shure/shared/angular/utils/logging';

import { SystemStatusApiService } from '../api/system-status-api.service';

import { SystemStatusSubscriptionGQL } from './graphql/generated/cloud-sys-api';
import { mapSystemStatusFromSysApi } from './mappers/map-system-status';

const DEFAULT_SYSTEM_STATUS: SystemStatus = {
	state: SystemState.Operational,
	writeSafe: true,
	schemaChanged: false
};

@Injectable({ providedIn: 'root' })
export class SysApiSystemStatusApiService implements SystemStatusApiService {
	private destroy$ = new Subject<void>();
	private readonly logger: ILogger;
	private readonly systemStatusSubject$ = new Subject<SystemStatus>();
	private readonly subscriptionManager = new SubscriptionManager({
		subscriptionType: 'global',
		retryWaitMs: 10_000,
		maxRetryAttempts: 3,
		create: (config): Subscription => this.createSystemStatusSubscription(config)
	});

	constructor(
		logger: ILogger,
		private readonly oktaService: OktaInterfaceService,
		private readonly systemStatusSubscription: SystemStatusSubscriptionGQL,
		private readonly appUpdateService: AppUpdateService
	) {
		this.logger = logger.createScopedLogger('SysApiSystemStatusApiService');

		monitorLoginState(this.oktaService, {
			onLogIn: () => this.initService(),
			onLogOut: () => this.suspendService()
		});
	}

	public getSystemStatus$(): Observable<SystemStatus> {
		return this.systemStatusSubject$.asObservable();
	}

	private initService(): void {
		this.destroy$ = new Subject<void>();
		this.subscriptionManager.register(['systemStatus']);
		this.getSystemStatus$().subscribe((systemStatus) => {
			this.appUpdateService.handleSystemStatusChange(systemStatus);
		});
	}

	private suspendService(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.subscriptionManager.deregisterAll();
	}

	private createSystemStatusSubscription(config: SubscriptionManagerConfigCreate): Subscription {
		return this.systemStatusSubscription
			.subscribe({ variables: {}, errorPolicy: 'ignore' })
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (result) => {
					this.systemStatusSubject$.next(
						mapSystemStatusFromSysApi(result.data?.systemStatus ?? DEFAULT_SYSTEM_STATUS)
					);
				},
				// in case the BE closes the WS, invoke the retry logic.
				complete: () => {
					this.logger.error('systemStatusSubscriptionGQL', 'subscription completed. Will retry.');
					config.retryCallback();
				},
				error: (error: unknown) => {
					if (apolloErrorContains(error, nonRetryableErrorSubStrings)) {
						return;
					}
					this.logger.error(
						'systemStatusSubscriptionGQL',
						'Encountered retryable error',
						JSON.stringify({ error }, null, 3)
					);
					config.retryCallback();
				}
			});
	}
}
